import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, Layout, Modal, notification, PageHeader, Space, Switch } from 'antd'
import idx from 'idx'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Route, useHistory } from 'react-router-dom'
import { fetchPageById, invalidateCFCache, newSuiteCardModule, removePageById, resetUpdatePageStatus, updatePage } from '~actions'
import { PageBasicInfo, ResultModal } from '~components'
import { getCurrentUser } from '~context'
import { Spinner } from '~stories'
import { getFileName } from '~utils'
import { validatePage } from '~validators'

import { createEmptyUniversalModule } from '../../components/UniversalTemplateModules/universalTemplateDefaults'
import { MSG_CF, PAGE_TYPES, PAGE_TYPES_PREMIUM_EDITOR } from '../../constants'
import { buildFaqSlugFromVenue } from '../../utils/faqSlugFromVenueName'
import { buildFaqPageUpdatePayload, sanitizePagePayload } from '../../utils/sanitizePagePayload'
import { getFormUrlValidationMessage } from '../../utils/universalTemplateFormValidation'
import {
  buildUniversalTemplateSlugFromName,
  getUniversalTemplateSlugPrefix,
  getUniversalTemplateSlugSuffix,
  isValidUniversalTemplateSlug,
  normalizeUniversalTemplateSubtype,
  UNIVERSAL_TEMPLATE_SLUG_SUFFIX_RE
} from '../../utils/universalTemplateSlugFromName'
import styles from './EditPage.module.css'

const NOTIFICATION_MESSAGES = {
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  DATA_FIELD_ERR: 'Missing required Fields'
}
const CONTENT_MODULE = {
  cta: {
    name: '',
    url: ''
  },
  description: '',
  header: '',
  icon: ''
}

const SUITE_CARD_MODULE = {
  title: '',
  subtitle: '',
  label: '',
  type: '',
  image: {},
  ctas: [],
  related_events: [],
  promotedEvents: [],
  filters: {}
}

const { REACT_APP_STAGE } = process.env

const EditPage = ({
  allSuiteLevels,
  availableEventsForPromotion,
  fetchPage,
  newSuiteCardModule,
  page,
  gallery: galleryProps,
  galleryHeader,
  removePageById,
  resetUpdatePageStatus,
  route,
  suitesAmenities,
  suitesCapacity,
  suitesEventIdeas,
  suitesEventType,
  vlpHeroAllEvents: vlpHeroAllEventsProps,
  promotedEvents,
  suitesHospitality,
  recommendedEvents,
  todaysEvent,
  tourVenues,
  upcomingEvents,
  suitesSize,
  updateMessage,
  updatePage,
  updateStatus,
  venues
}) => {
  const history = useHistory()
  const [isDataLoaded, setIsDataLoaded] = useState()
  const [pageId, setPageId] = useState('')
  const [pageToEdit, setPageToEdit] = useState({})
  const [vlpHeroAllEvents, setVLPHeroAllEvents] = useState([])
  const [vlpHeroPromotedEvents, setVLPHeroPromotedEvents] = useState([])
  const { access_token: accessToken, userDetails: { isContentEditor } } = getCurrentUser()
  const [gallery, setGallery] = useState([])
  const [gallerySectionTitle, setGallerySectionTitle] = useState('')
  const [hydratedForPageId, setHydratedForPageId] = useState(null)
  const updateRequestedRef = useRef(false)
  // const [isDataValid, setIsDataValid] = useState(true)

  useEffect(() => {
    if (vlpHeroAllEventsProps) {
      setVLPHeroAllEvents(vlpHeroAllEventsProps)
    }
  }, [vlpHeroAllEventsProps])

  useEffect(() => {
    if (promotedEvents) {
      setVLPHeroPromotedEvents(promotedEvents)
    }
  }, [promotedEvents])

  useEffect(() => {
    newSuiteCardModule(accessToken)
  }, [accessToken, newSuiteCardModule])

  useEffect(() => {
    const routePageId = idx(route, _ => _.match.params.id)
    if (routePageId) {
      resetUpdatePageStatus()
      updateRequestedRef.current = false
      setIsDataLoaded(false)
      setHydratedForPageId(null)
      fetchPage(routePageId)
      setPageId(routePageId)
    }
  }, [route, fetchPage, resetUpdatePageStatus])

  const routePageId = idx(route, _ => _.match.params.id)
  const isFaqPageType = pageToEdit?.type === 'faq' || page?.type === 'faq'

  // Hydrate pageToEdit once per fetch/navigation. Pre-FAQ behavior merged gallery for tours
  // on load; re-running on every Redux `page` update wiped in-progress FAQ section edits.
  useEffect(() => {
    if (!routePageId) {
      return
    }
    if (!page?._id || String(page._id) !== String(routePageId)) {
      return
    }
    if (hydratedForPageId === routePageId) {
      return
    }

    if (page.type === 'tours') {
      setGallery(galleryProps || [])
      setGallerySectionTitle(galleryHeader || '')
      setPageToEdit({
        ...page,
        gallery: galleryProps || [],
        galleryHeader: galleryHeader || ''
      })
    } else {
      setPageToEdit(page.type === 'faq'
        ? { ...page, sections: Array.isArray(page.sections) ? page.sections : [] }
        : page)
    }
    setHydratedForPageId(routePageId)
    setIsDataLoaded(true)
  }, [page, routePageId, galleryProps, galleryHeader, hydratedForPageId])

  useEffect(() => {
    if (updateStatus && updateMessage) {
      setIsDataLoaded(true)
      if (updateRequestedRef.current && pageId) {
        updateRequestedRef.current = false
        if (isFaqPageType) {
          setHydratedForPageId(null)
          fetchPage(pageId)
        }
      }
    } else if (!updateStatus && updateMessage) {
      setIsDataLoaded(true)
      updateRequestedRef.current = false
      notification.error({ message: updateMessage })
      resetUpdatePageStatus()
    }
  }, [updateStatus, updateMessage, resetUpdatePageStatus, isFaqPageType, pageId, fetchPage])

  // const validateFields = () => {
  //   if (!pageToEdit.social_feeds.header && !pageToEdit.social_feeds.embed_url) {
  //     return false
  //   }
  //   if (!pageToEdit.upcoming_shows_section_title && !pageToEdit.recommendations_section_title) {
  //     return false
  //   }
  //   if (!pageToEdit.banner.title && !pageToEdit.banner.image.url && !pageToEdit.banner.cta.url && !pageToEdit.banner.cta.name) {
  //     return false
  //   }
  //   if (Object.entries(pageToEdit.category_pills).map((value) => { return value.some(i => (i.text && i.url && i.icon)) }).some(i => i === false)) {
  //     return false
  //   }
  //   if (Object.entries(pageToEdit.media_row).map((value) => { return value.some(i => (i.title && i.url)) }).some(i => i === false)) {
  //     return false
  //   }
  //   if (Object.entries(pageToEdit.marketing_pills).map((value) => { return value.some(i => (i.text && i.url && i.icon)) }).some(i => i === false)) {
  //     return false
  //   }
  //   if (Object.entries(pageToEdit.content_cards.cards).map((value) => { return value.some(i => (i.title && i.cta.url && i.cta.name)) }).some(i => i === false)) {
  //     return false
  //   }
  //   return true
  // }
  const handleSubmit = async () => {
    if (pageToEdit?.type === 'universal-template') {
      const hasErrors = Object.values(universalTemplateErrors).some(Boolean)
      if (hasErrors) {
        return
      }
    }

    if (PAGE_TYPES_PREMIUM_EDITOR.includes(pageToEdit?.type)) {
      const { isValid, message } = validatePage(pageToEdit)

      if (!isValid) {
        notification.error({ message })
        return
      }
    }

    if (!isContentEditor) {
      notification.error({ message: NOTIFICATION_MESSAGES.UNAUTHORIZED })
      return
    }

    // if (!validateFields()) {
    //   setIsDataValid(true)
    //   notification.error({ message: NOTIFICATION_MESSAGES.DATA_FIELD_ERR })
    //   return
    // } else {
    //   setIsDataValid(true)
    // }

    setIsDataLoaded(false)
    const isFaqPage = pageToEdit?.type === 'faq' || page?.type === 'faq'
    const payload = isFaqPage
      ? buildFaqPageUpdatePayload({ ...pageToEdit, type: 'faq' })
      : sanitizePagePayload({ ...pageToEdit, vlpHeroPromotedEvents })
    const targetPageId = routePageId || pageId
    if (!targetPageId) {
      setIsDataLoaded(true)
      notification.error({ message: 'Page id is missing; reload the edit page and try again.' })
      return
    }
    updateRequestedRef.current = true
    updatePage(targetPageId, payload, accessToken)

    if (REACT_APP_STAGE === 'production') {
      await invalidateCFCache(MSG_CF.PROD, pageToEdit?.slug, true)
    } else {
      await invalidateCFCache(MSG_CF.NON_PROD, pageToEdit?.slug, true)
    }
  }

  const keepEditingPage = (
    <Route key="editPages" render={({ history }) => (
      <Button
        onClick={() => {
          resetUpdatePageStatus()
          history.push(`/pages/${pageId}/edit`)
        }}
        type="primary"
      >
        Keep Editing
      </Button>
    )} />
  )

  const goToListPagesButton = (
    <Route key="listPages" render={({ history }) => (
      <Button
        onClick={() => {
          resetUpdatePageStatus()
          history.push('/pages')
        }}
        type="primary"
      >
        Go to Page List
      </Button>
    )} />
  )

  const handleModalClose = () => {
    resetUpdatePageStatus()
  }

  const confirmDeleteUniversalPage = () => {
    Modal.confirm({
      title: 'Delete this page?',
      content: 'This permanently removes the universal template page. This cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        const result = await removePageById(pageId, accessToken)
        if (result && result.success !== false) {
          notification.success({ message: result.message || 'Page deleted.' })
          history.push('/pages')
          return
        }
        notification.error({ message: result?.message || 'Could not delete page.' })
        return Promise.reject(new Error(result?.message || 'Delete failed'))
      }
    })
  }

  const handlePageNameChange = event => {
    const name = event.target.value
    setPageToEdit(prev => {
      const next = { ...prev, name }
      const slugEmpty = !String(prev?.slug ?? '').trim()
      if (prev?.type === 'universal-template' && venues?.length && slugEmpty) {
        const slug = buildUniversalTemplateSlugFromName(name, prev.venue_id, venues, prev?.subtype)
        if (slug !== undefined) {
          next.slug = slug
        }
      }
      return next
    })
  }

  const handleSlugChange = event => {
    const slug = event.target.value
    setPageToEdit(prev => ({
      ...prev,
      slug
    }))
  }

  const handleGalleryChange = (gallery) => {
    setGallery(gallery)
    setPageToEdit({
      ...pageToEdit,
      gallery
    })
  }

  const addContentModule = (event) => {
    event.stopPropagation()
    const { content_modules: contentModules = [] } = pageToEdit
    setPageToEdit({
      ...pageToEdit,
      content_modules: [
        ...contentModules,
        CONTENT_MODULE
      ]
    })
  }

  const addSuitesCardModule = (event) => {
    event.stopPropagation()
    const { cards: suiteCards = [] } = pageToEdit
    setPageToEdit({
      ...pageToEdit,
      cards: [
        ...suiteCards,
        {
          suitesAllEvents: availableEventsForPromotion,
          related_events: [],
          promotedEvents: [],
          ...SUITE_CARD_MODULE
        }
      ]
    })
  }

  const deleteGallery = (event, dataFieldGalleryIndex) => {
    event.stopPropagation()

    const clonedGalleries = [...gallery]

    if (dataFieldGalleryIndex !== -1) {
      clonedGalleries.splice(dataFieldGalleryIndex, 1)
    }
    setGallery(clonedGalleries)
    setPageToEdit({
      ...pageToEdit,
      gallery: clonedGalleries
    })
  }

  const deleteAllGallery = (event) => {
    event.stopPropagation()

    setGallery([])
    setPageToEdit({
      ...pageToEdit,
      gallery: []
    })
  }

  const deleteAllPriceCards = (event) => {
    event.stopPropagation()
    const { tours_prices: tourPrices = {} } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      tours_prices: {
        ...tourPrices,
        cards: []
      }
    })
  }

  const deleteAllFAQs = (event) => {
    event.stopPropagation()

    const { faqs = {} } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      faqs: {
        ...faqs,
        flex_modules: []
      }
    })
  }

  const handleGalleryImageAltChange = (event, galleryIndex) => {
    const { gallery = [] } = pageToEdit
    const updatedGallery = [
      ...gallery.slice(0, galleryIndex),
      {
        ...gallery[galleryIndex],
        alt: event.target.value
      },
      ...gallery.slice(galleryIndex + 1)
    ]
    setGallery(updatedGallery)
    setPageToEdit({
      ...pageToEdit,
      gallery: updatedGallery
    })
  }

  const handleGalleryImageUrlUpdate = (imageUrl, galleryIndex) => {
    const { gallery = [] } = pageToEdit

    const updatedGallery = [
      ...gallery.slice(0, galleryIndex),
      {
        ...gallery[galleryIndex],
        src: imageUrl
      },
      ...gallery.slice(galleryIndex + 1)
    ]
    setGallery(updatedGallery)
    setPageToEdit({
      ...pageToEdit,
      gallery: updatedGallery
    })
  }

  const handleGallerySubTitleChange = (event, galleryIndex) => {
    const { gallery = [] } = pageToEdit
    const updatedGallery = [
      ...gallery.slice(0, galleryIndex),
      {
        ...gallery[galleryIndex],
        subtitle: event.target.value
      },
      ...gallery.slice(galleryIndex + 1)
    ]
    setGallery(updatedGallery)
    setPageToEdit({
      ...pageToEdit,
      gallery: updatedGallery
    })
  }

  const handleGalleryTitleChange = (event, galleryIndex) => {
    const { gallery = [] } = pageToEdit
    const updatedGallery = [
      ...gallery.slice(0, galleryIndex),
      {
        ...gallery[galleryIndex],
        title: event.target.value
      },
      ...gallery.slice(galleryIndex + 1)
    ]
    setGallery(updatedGallery)
    setPageToEdit({
      ...pageToEdit,
      gallery: updatedGallery
    })
  }

  const handleGalleryMediaChange = (selectedItems, galleryIndex) => {
    const { gallery = [] } = pageToEdit
    const updatedGallery = [
      ...gallery.slice(0, galleryIndex),
      {
        ...gallery[galleryIndex],
        mediaType: selectedItems
      },
      ...gallery.slice(galleryIndex + 1)
    ]
    setGallery(updatedGallery)
    setPageToEdit({
      ...pageToEdit,
      gallery: updatedGallery
    })
  }

  const addGallery = (event) => {
    event.stopPropagation()
    const { gallery = [] } = pageToEdit

    gallery.push({
      title: '',
      src: '',
      alt: '',
      mediaType: '',
      subtitle: ''
    })
    setGallery(gallery)
    setPageToEdit({
      ...pageToEdit,
      gallery
    })
  }

  const handleContentModuleHeaderChange = (event, index) => {
    const header = event.target.value
    const { content_modules: contentModules = [] } = pageToEdit
    contentModules[index].header = header
    setPageToEdit({
      ...pageToEdit,
      content_modules: [
        ...contentModules
      ]
    })
  }

  const handleContentCardSectionHeaderChange = (event) => {
    const { content_cards: contentCards = {} } = pageToEdit
    setPageToEdit({
      ...pageToEdit,
      content_cards: {
        ...contentCards,
        header: event.target.value
      }
    })
  }

  const handleContentModuleContentChange = (paragraph, index) => {
    const { content_modules: contentModules = [] } = pageToEdit
    if (paragraph.length < 156) {
      contentModules[index].description = paragraph
      setPageToEdit({
        ...pageToEdit,
        content_modules: [
          ...contentModules
        ]
      })
    }
  }

  const handleContentModuleImageUrlUpdate = (secureImageURL, index) => {
    const { content_modules: contentModules = [] } = pageToEdit
    contentModules[index].icon = secureImageURL
    setPageToEdit({
      ...pageToEdit,
      content_modules: [
        ...contentModules
      ]
    })
  }

  const handleContentModuleCTAUrlChange = (event, index) => {
    const { content_modules: contentModules = [] } = pageToEdit
    contentModules[index] = {
      ...contentModules[index],
      cta: {
        ...contentModules[index].cta,
        url: event.target.value
      }
    }
    setPageToEdit({
      ...pageToEdit,
      content_modules: [
        ...contentModules
      ]
    })
  }

  const handleContentModuleCTANameChange = (event, index) => {
    const { content_modules: contentModules = [] } = pageToEdit
    contentModules[index] = {
      ...contentModules[index],
      cta: {
        ...contentModules[index].cta,
        name: event.target.value
      }
    }
    setPageToEdit({
      ...pageToEdit,
      content_modules: [
        ...contentModules
      ]
    })
  }

  const deleteContentModule = (event, index) => {
    event.stopPropagation()
    const { content_modules: contentModules = [] } = pageToEdit
    const updatedContentModules = contentModules.filter((_, i) => i !== index)
    setPageToEdit({
      ...pageToEdit,
      content_modules: [
        ...updatedContentModules
      ]
    })
  }

  const deleteSuitesCardModule = (event, index) => {
    event.stopPropagation()
    const { cards: suiteCards = [] } = pageToEdit
    const updatedCards = suiteCards.filter((_, i) => i !== index)
    setPageToEdit({
      ...pageToEdit,
      cards: [
        ...updatedCards
      ]
    })
  }

  const handleSuiteAmenitiesChange = (amenities, index) => {
    const { cards: suiteCard = [] } = pageToEdit
    const { filters } = suiteCard[index]
    setPageToEdit({
      ...pageToEdit,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          filters: {
            ...filters,
            amenities
          }
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteCapacityChange = (capacity, index) => {
    const { cards: suiteCard = [] } = pageToEdit
    const { filters } = suiteCard[index]

    setPageToEdit({
      ...pageToEdit,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          filters: {
            ...filters,
            capacity
          }
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteEventIdeasChange = (eventideas, index) => {
    const { cards: suiteCard = [] } = pageToEdit
    const { filters } = suiteCard[index]

    setPageToEdit({
      ...pageToEdit,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          filters: {
            ...filters,
            eventideas
          }
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleAddCta = (event, index) => {
    event.stopPropagation()
    const { cards = [] } = pageToEdit
    const updatedCards = [...cards]
    updatedCards[index].ctas = [
      ...updatedCards[index].ctas,
      {
        title: '',
        href: ''
      }
    ]
    setPageToEdit({
      ...pageToEdit,
      cards: updatedCards
    })
  }

  const handleDeleteCta = (event, ctaIndex, suiteIndex) => {
    event.stopPropagation()
    const { cards = [] } = pageToEdit
    const updatedCards = [...cards]
    const clonedCTAs = updatedCards[suiteIndex].ctas

    if (ctaIndex !== -1) {
      clonedCTAs.splice(ctaIndex, 1)
    }
    setPageToEdit({
      ...pageToEdit,
      cards: updatedCards
    })
  }

  const handleSuiteEventTypeChange = (eventtype, index) => {
    const { cards: suiteCard = [] } = pageToEdit
    const { filters } = suiteCard[index]

    setPageToEdit({
      ...pageToEdit,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          filters: {
            ...filters,
            eventtype
          }
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteHospitalityChange = (hospitalitytype, index) => {
    const { cards: suiteCard = [] } = pageToEdit
    const { filters } = suiteCard[index]

    setPageToEdit({
      ...pageToEdit,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          filters: {
            ...filters,
            hospitalitytype
          }
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteSizeChange = (size, index) => {
    const { cards: suiteCard = [] } = pageToEdit
    const { filters } = suiteCard[index]

    setPageToEdit({
      ...pageToEdit,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          filters: {
            ...filters,
            size
          }
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteLogoUpload = (secureImageURL, index) => {
    const { cards: suiteCard = [] } = pageToEdit
    const alt = getFileName(secureImageURL)

    setPageToEdit({
      ...pageToEdit,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          image: {
            alt,
            src: secureImageURL
          }
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteCardTypeChange = (type, index) => {
    const { cards: suiteCard = [] } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          type,
          suitesAllEvents: availableEventsForPromotion,
          related_events: [],
          promotedEvents: []
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteCardShortNameChange = (shortDesc, index) => {
    const { cards: suiteCard = [] } = pageToEdit
    const [filteredAllEvents] = allSuiteLevels.filter(suite => suite.type === shortDesc)
    const allEventItems = shortDesc ? filteredAllEvents?.allEvents : availableEventsForPromotion
    const allEventsFiltered = shortDesc ? filteredAllEvents?.events : []
    setPageToEdit({
      ...pageToEdit,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          short_desc: shortDesc || '',
          suitesAllEvents: allEventItems,
          related_events: allEventsFiltered.map(({ id }) => id),
          promotedEvents: allEventsFiltered
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteSubTitleChange = (event, index) => {
    const { cards: suiteCard = [] } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          subtitle: event.target.value
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteCTAUpdate = (ctas, index) => {
    const { cards: suiteCard = [] } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          subtitle: event.target.value
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteFormStatus = (event, index) => {
    const { cards: suiteCard = [] } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          show_form: event.target.checked
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteTitleChange = (event, index) => {
    const { cards: suiteCard = [] } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          title: event.target.value
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleHeroTitleChange = event => {
    const title = event.target.value
    const { hero } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      hero: {
        ...hero,
        title
      }
    })
  }

  const handleHeroButtonLabelChange = event => {
    const buttonName = event.target.value
    const { hero } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      hero: {
        ...hero,
        button_name: buttonName
      }
    })
  }
  const handleHeroPhoneNumberChange = event => {
    const phoneNumber = event.target.value
    const { hero } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      hero: {
        ...hero,
        phone_number: phoneNumber
      }
    })
  }

  const handleHeroDescriptionChange = description => {
    const { hero } = pageToEdit

    if (description.length < 156) {
      setPageToEdit((pageToEdit) => ({
        ...pageToEdit,
        hero: {
          ...hero,
          description
        }
      }))
    }
  }
  const handleHeroSubTitleChange = event => {
    const subTitle = event.target.value
    const { hero } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      hero: {
        ...hero,
        sub_title: subTitle
      }
    })
  }
  const handleHeroImageUrlUpdate = (thumbnailImageUrl, secureImageURL) => {
    const imageUrl = secureImageURL || thumbnailImageUrl
    const { hero } = pageToEdit

    const alt = getFileName(imageUrl)
    const { image } = hero
    setPageToEdit({
      ...pageToEdit,
      hero: {
        ...hero,
        image: {
          ...image,
          alt,
          src: imageUrl
        }
      }
    })
  }
  const handleHeroChatLabelChange = event => {
    const chatButton = event.target.value
    const { hero } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      hero: {
        ...hero,
        chat_button: chatButton
      }
    })
  }

  const handleHeroEmailChange = event => {
    const email = event.target.value
    const { hero } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      hero: {
        ...hero,
        email
      }
    })
  }

  const handlePageMetaDescriptionChange = (description) => {
    const { page_meta: pageMeta } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      page_meta: {
        ...pageMeta,
        description
      }
    })
  }

  const handlePageMetaHeadingChange = (event) => {
    const { page_meta: pageMeta } = pageToEdit
    const heading = event.target.value

    setPageToEdit({
      ...pageToEdit,
      page_meta: {
        ...pageMeta,
        heading
      }
    })
  }

  const handleVenueChange = (selectedVenue) => {
    setPageToEdit(prev => {
      const next = { ...prev, venue_id: selectedVenue }
      if (prev?.type === 'universal-template' && venues?.length && selectedVenue) {
        const slug = buildUniversalTemplateSlugFromName(prev.name, selectedVenue, venues, prev?.subtype)
        if (slug !== undefined) {
          next.slug = slug
        }
      } else if (prev?.type === 'faq' && venues?.length && selectedVenue) {
        const [filteredVenue] = venues.filter(venue => venue._id === selectedVenue)
        if (filteredVenue) {
          next.slug = buildFaqSlugFromVenue(filteredVenue.name, 'faqs')
          next.venue = { name: filteredVenue.name, slug: filteredVenue.slug }
        }
      }
      return next
    })
  }

  const handlePageTypeChange = selectedType => {
    setPageToEdit({
      ...pageToEdit,
      type: selectedType
    })
  }

  const handlePageSubtypeChange = value => {
    const subtype = value || ''
    const next = { ...pageToEdit, subtype }
    if (pageToEdit?.type === 'universal-template' && pageToEdit?.slug) {
      const suf = getUniversalTemplateSlugSuffix(pageToEdit.slug, pageToEdit.subtype)
      if (suf && UNIVERSAL_TEMPLATE_SLUG_SUFFIX_RE.test(suf)) {
        const seg = normalizeUniversalTemplateSubtype(subtype || undefined)
        next.slug = `${getUniversalTemplateSlugPrefix(seg)}${suf}`
      }
    }
    setPageToEdit(next)
  }

  const handlePageMetaKeywordsChange = (event) => {
    const keywords = event.target.value
    const { page_meta: pageMeta } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      page_meta: {
        ...pageMeta,
        keywords
      }
    })
  }

  const handlePageMetaShowSEOChange = (event) => {
    const showSEO = event.target.checked
    const { page_meta: pageMeta } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      page_meta: {
        ...pageMeta,
        should_show_seo: showSEO
      }
    })
  }

  const handlePageMetaTextChange = (text) => {
    const { page_meta: pageMeta } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      page_meta: {
        ...pageMeta,
        text
      }
    })
  }

  const handlePageMetaTitleChange = (event) => {
    const title = event.target.value
    const { page_meta: pageMeta } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      page_meta: {
        ...pageMeta,
        title
      }
    })
  }

  const handleFormModuleIdUpdate = (event) => {
    const formstackId = event.target.value

    const { form } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      form: {
        ...form,
        formstack_id: formstackId
      }
    })
  }

  const handleFormModuleDescriptionChange = (description) => {
    const { form } = pageToEdit

    if (description.length < 156) {
      setPageToEdit((pageToEdit) => ({
        ...pageToEdit,
        form: {
          ...form,
          description
        }
      }))
    }
  }

  const handleFormModuleHeaderChange = (header) => {
    const { form } = pageToEdit

    if (header.length < 71) {
      setPageToEdit((pageToEdit) => ({
        ...pageToEdit,
        form: {
          ...form,
          header
        }
      }))
    }
  }

  const handleFormModuleDisclaimerChange = (disclaimer) => {
    const { form } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      form: {
        ...form,
        disclaimer
      }
    })
  }

  const handleButtonTextUpdate = (event) => {
    const submitButtonText = event.target.value
    const { form } = pageToEdit
    setPageToEdit({
      ...pageToEdit,
      form: {
        ...form,
        submit_button_text: submitButtonText
      }
    })
  }

  const handleFormModuleTitleChange = (title) => {
    const { form } = pageToEdit
    const { success_screen: successScreen } = form
    setPageToEdit({
      ...pageToEdit,
      form: {
        ...form,
        success_screen: {
          ...successScreen,
          title: title
        }
      }
    })
  }

  const handleFormModuleMessageChange = (message) => {
    const { form } = pageToEdit
    const { success_screen: successScreen } = form
    setPageToEdit({
      ...pageToEdit,
      form: {
        ...form,
        success_screen: {
          ...successScreen,
          message
        }
      }
    })
  }

  const handleCTAHrefChange = (href, suiteIndex, ctaIndex) => {
    const { cards = [] } = pageToEdit
    const updatedCards = [...cards]
    const clonedCTAs = updatedCards[suiteIndex].ctas

    if (ctaIndex !== -1 && clonedCTAs[ctaIndex]) {
      clonedCTAs[ctaIndex].href = href
    }
    setPageToEdit({
      ...pageToEdit,
      cards: updatedCards
    })
  }

  const handleCTATitleChange = (title, suiteIndex, ctaIndex) => {
    const { cards = [] } = pageToEdit
    const updatedCards = [...cards]
    const clonedCTAs = updatedCards[suiteIndex].ctas

    if (ctaIndex !== -1 && clonedCTAs[ctaIndex]) {
      clonedCTAs[ctaIndex].title = title
    }
    setPageToEdit({
      ...pageToEdit,
      cards: updatedCards
    })
  }

  const handleFormModuleSuccessSubmitButtonTextUpdate = (event) => {
    const successScreenButtonText = event.target.value
    const { form } = pageToEdit
    const { success_screen: successScreen } = form
    setPageToEdit({
      ...pageToEdit,
      form: {
        ...form,
        success_screen: {
          ...successScreen,
          button_text: successScreenButtonText
        }
      }
    })
  }

  const handleShowCalendar = (event) => {
    event.stopPropagation()

    const { calendar } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      calendar: {
        ...calendar,
        show_calendar: event.target.checked
      }
    })
  }

  const handleCalendarTitleChange = event => {
    const title = event.target.value
    const { calendar } = pageToEdit
    setPageToEdit({
      ...pageToEdit,
      calendar: {
        ...calendar,
        title
      }
    })
  }

  const handleCalendarDescriptionChange = (description) => {
    const { calendar } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      calendar: {
        ...calendar,
        description
      }
    })
  }

  const handleOverviewDescriptionChange = (description) => {
    const { overview } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      overview: {
        ...overview,
        html_description: description
      }
    })
  }

  const handleAllEventsChange = (events, suiteIndex) => {
    const { cards: suiteCards = [] } = pageToEdit
    if (suiteCards[suiteIndex]) {
      suiteCards[suiteIndex].suitesAllEvents = [...events]
    }

    setPageToEdit({
      ...pageToEdit,
      cards: suiteCards
    })
  }

  const handleSelectedEventsChange = (selectedEvents, suiteIndex) => {
    const { cards: suiteCards = [] } = pageToEdit
    if (suiteCards[suiteIndex]) {
      suiteCards[suiteIndex].promotedEvents = [...selectedEvents]
      suiteCards[suiteIndex].related_events = selectedEvents.map(({ id }) => id)
    }

    setPageToEdit({
      ...pageToEdit,
      cards: suiteCards
    })
  }

  const handleHorizontalBanner = updatedBanner => {
    const { banner: clonedbanner } = pageToEdit
    setPageToEdit({
      ...pageToEdit,
      banner: {
        ...clonedbanner,
        ...updatedBanner
      }
    })
  }

  const reorderVLPHeroPromotedEvents = (eventToMove, direction, sourceIndex) => {
    setVLPHeroPromotedEvents(prev => {
      const currentIndex = Number.isInteger(sourceIndex)
        ? sourceIndex
        : prev.findIndex(event => {
          if (event === eventToMove) return true
          const eventKey = event?.id || event?._id || event?.slug || event?.title
          const targetKey = eventToMove?.id || eventToMove?._id || eventToMove?.slug || eventToMove?.title
          return Boolean(eventKey) && eventKey === targetKey
        })
      if (currentIndex === -1) return prev
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
      if (targetIndex < 0 || targetIndex >= prev.length) return prev
      const reordered = [...prev]
      ;[reordered[currentIndex], reordered[targetIndex]] = [reordered[targetIndex], reordered[currentIndex]]
      return reordered
    })
  }

  const addNewMediaIcon = (event) => {
    event.stopPropagation()
    const { media_row: clonedMediaRow } = pageToEdit
    const mediaIcon = {
      title: '',
      url: '',
      icon: {
        url: '',
        alt: ''
      }
    }
    setPageToEdit({
      ...pageToEdit,
      media_row: [...clonedMediaRow, mediaIcon]
    })
  }

  const addNewCategoryPill = (event) => {
    event.stopPropagation()
    const { category_pills: clonedPills } = pageToEdit
    const pillObj = {
      text: '',
      url: '',
      icon: ''
    }
    setPageToEdit({
      ...pageToEdit,
      category_pills: [...clonedPills, pillObj]
    })
  }

  const addNewMarketingPill = (event) => {
    event.stopPropagation()
    const { marketing_pills: clonedPills } = pageToEdit
    const pillObj = {
      text: '',
      url: '',
      icon: ''
    }
    setPageToEdit({
      ...pageToEdit,
      marketing_pills: [...clonedPills, pillObj]
    })
  }

  const addNewContentCardPill = (event) => {
    event.stopPropagation()
    const { content_cards: contentCards } = pageToEdit
    const cardObj = {
      title: '',
      subtitle: '',
      cta: {},
      image: {}
    }
    setPageToEdit({
      ...pageToEdit,
      content_cards: {
        ...contentCards,
        cards: [
          ...contentCards.cards,
          cardObj
        ]
      }
    })
  }

  const deleteAllCategoryPills = (event) => {
    event.stopPropagation()
    setPageToEdit({
      ...pageToEdit,
      category_pills: []
    })
  }

  const deleteAllMarketingPills = (event) => {
    event.stopPropagation()
    setPageToEdit({
      ...pageToEdit,
      marketing_pills: []
    })
  }

  const deleteAllMediaIcon = (event) => {
    event.stopPropagation()
    setPageToEdit({
      ...pageToEdit,
      media_row: []
    })
  }

  const handleMediaRow = mediaRows => {
    setPageToEdit({
      ...pageToEdit,
      media_row: mediaRows
    })
  }

  const handleMarketingPills = marketingPills => {
    setPageToEdit({
      ...pageToEdit,
      marketing_pills: marketingPills
    })
  }

  const handleContentCard = cards => {
    setPageToEdit({
      ...pageToEdit,
      content_cards: {
        ...pageToEdit.content_cards,
        cards
      }
    })
  }

  const handleAutoplay = (event) => {
    event.stopPropagation()

    const { hero } = pageToEdit

    setPageToEdit({
      ...pageToEdit,
      hero: {
        ...hero,
        autoplay: event.target.checked
      }
    })
  }

  const handleCategoryPills = categoryPills => {
    setPageToEdit({
      ...pageToEdit,
      category_pills: categoryPills
    })
  }

  const handleFlockler = updatedFlockler => {
    const { social_feeds: flockler } = pageToEdit
    setPageToEdit({
      ...pageToEdit,
      social_feeds: {
        ...flockler,
        ...updatedFlockler
      }
    })
  }

  const handleRecommendationSectionTitle = (event) => {
    setPageToEdit({
      ...pageToEdit,
      recommendations_section_title: event.target.value
    })
  }

  const handleGallerySectionTitle = (event) => {
    setGallerySectionTitle(event.target.value)
    setPageToEdit({
      ...pageToEdit,
      galleryHeader: event.target.value
    })
  }

  const handleUpcomingShowsSectionTitle = (event) => {
    setPageToEdit({
      ...pageToEdit,
      upcoming_shows_section_title: event.target.value
    })
  }

  const handleImageUrlChange = (secureImageURL) => {
    const { hero } = pageToEdit
    const alt = getFileName(secureImageURL)
    const { image } = hero
    setPageToEdit({
      ...pageToEdit,
      hero: {
        ...hero,
        image: {
          ...image,
          alt,
          src: secureImageURL
        }
      }
    })
  }

  const addNewLink = (event) => {
    event.stopPropagation()
    let { anchor_links: clonedLinks } = pageToEdit
    const linkObj = {
      label: '',
      link: ''
    }
    if (!clonedLinks) {
      clonedLinks = []
    }
    setPageToEdit({
      ...pageToEdit,
      anchor_links: [...clonedLinks, linkObj]
    })
  }

  const deleteAllLink = (event) => {
    event.stopPropagation()
    setPageToEdit({
      ...pageToEdit,
      anchor_links: []
    })
  }

  const setAnchorLinks = links => {
    setPageToEdit({
      ...pageToEdit,
      anchor_links: [
        ...links
      ]
    })
  }

  const handleImageChange = (imgObj) => {
    const { hero } = pageToEdit
    setPageToEdit({
      ...pageToEdit,
      hero: {
        ...hero,
        image: imgObj
      }
    })
  }

  const addNewMarketingModule = (event) => {
    event.stopPropagation()
    const { marketing_modules: clonedPills } = pageToEdit
    const pillObj = {
      text: '',
      url: '',
      icon: ''
    }
    setPageToEdit({
      ...pageToEdit,
      marketing_modules: [...clonedPills, pillObj]
    })
  }

  const addNewFAQ = (event) => {
    event.stopPropagation()
    const { faqs: faqsCards } = pageToEdit
    const cardObj = {
      title: '',
      subtitle: '',
      cta: {},
      images: []
    }
    setPageToEdit({
      ...pageToEdit,
      faqs: {
        ...faqsCards,
        flex_modules: [
          ...faqsCards?.flex_modules,
          cardObj
        ]
      }
    })
  }

  const addNewFAQImage = (card, rowIndex) => {
    const { faqs: faqsCards } = pageToEdit
    const cardImgObj = {
      url: '',
      alt: '',
      href: ''
    }
    faqsCards?.flex_modules?.[rowIndex]?.images?.push(cardImgObj)

    console.log(faqsCards)
    setPageToEdit({
      ...pageToEdit,
      faqs: {
        ...faqsCards
      }
    })
  }

  const deleteAllFAQsImages = (rowIndex) => {
    const { faqs: faqsCards } = pageToEdit
    const { flex_modules: flexModules } = faqsCards

    setPageToEdit({
      ...pageToEdit,
      faqs: {
        ...faqsCards,
        flex_modules: [
          ...flexModules.slice(0, rowIndex),
          {
            ...flexModules[rowIndex],
            images: []
          },
          ...flexModules.slice(rowIndex + 1)
        ]
      }
    })
  }

  const deleteAllMarketingModules = (event) => {
    event.stopPropagation()
    setPageToEdit({
      ...pageToEdit,
      marketing_modules: []
    })
  }

  const handleMarketingModules = marketingModules => {
    setPageToEdit({
      ...pageToEdit,
      marketing_modules: marketingModules
    })
  }

  const handlePriceCardSectionHeaderChange = (event) => {
    const { tours_prices: tourPrices = {} } = pageToEdit
    setPageToEdit({
      ...pageToEdit,
      tours_prices: {
        ...tourPrices,
        header: event.target.value
      }
    })
  }

  const handleOverviewSectionHeaderChange = (event) => {
    const { overview = {} } = pageToEdit
    setPageToEdit({
      ...pageToEdit,
      overview: {
        ...overview,
        header: event.target.value
      }
    })
  }

  const handleFAQsSectionHeaderChange = (event) => {
    const { faqs: faqsCards } = pageToEdit
    setPageToEdit({
      ...pageToEdit,
      faqs: {
        ...faqsCards,
        header: event.target.value
      }
    })
  }

  const deleteAllPriceCard = (event) => {
    event.stopPropagation()
    setPageToEdit({
      ...pageToEdit,
      tours_prices: []
    })
  }
  const addPriceCard = (event) => {
    event.stopPropagation()
    const { tours_prices: priceCards } = pageToEdit
    const cardObj = {
      title: '',
      subtitle: '',
      cta: {},
      image: {}
    }
    setPageToEdit({
      ...pageToEdit,
      tours_prices: {
        ...priceCards,
        cards: [
          ...priceCards.cards,
          cardObj
        ]
      }
    })
  }

  const handlePriceCardDisclaimerChange = (disclaimer) => {
    const { tours_prices: tourPrices = {} } = pageToEdit
    setPageToEdit({
      ...pageToEdit,
      tours_prices: {
        ...tourPrices,
        disclaimer
      }
    })
  }

  const handleTourPlanDescriptionChange = (description) => {
    const { tours_prices: tourPrices = {} } = pageToEdit
    setPageToEdit({
      ...pageToEdit,
      tours_prices: {
        ...tourPrices,
        html_description: description
      }
    })
  }

  const handlePriceCard = cards => {
    setPageToEdit({
      ...pageToEdit,
      tours_prices: {
        ...pageToEdit.tours_prices,
        cards
      }
    })
  }

  const handleFAQs = cards => {
    setPageToEdit({
      ...pageToEdit,
      faqs: {
        ...pageToEdit.faqs,
        flex_modules: cards
      }
    })
  }

  const handleFAQPageSections = (sectionsOrUpdater) => {
    setPageToEdit(prev => ({
      ...prev,
      sections: typeof sectionsOrUpdater === 'function'
        ? sectionsOrUpdater(prev.sections || [])
        : sectionsOrUpdater
    }))
  }

  const handleFAQAttending = (field, value) => {
    setPageToEdit(prev => {
      const attending = { ...(prev.attending || {}) }
      if (field === 'cta_label') {
        attending.cta = { ...(attending.cta || {}), label: value }
      } else if (field === 'cta_href') {
        attending.cta = { ...(attending.cta || {}), href: value }
      } else {
        attending[field] = value
      }
      return {
        ...prev,
        attending
      }
    })
  }

  const handleFAQHeroImageUpload = (url) => {
    setPageToEdit(prev => ({
      ...prev,
      hero: {
        ...(prev.hero || {}),
        image: { src: url, alt: url.split('/').pop() }
      }
    }))
  }

  const handleFAQHeroTitleChange = (title) => {
    setPageToEdit(prev => ({
      ...prev,
      hero: {
        ...(prev.hero || {}),
        title
      }
    }))
  }

  const addNewPriceCard = (event) => {
    event.stopPropagation()
    const { tours_prices: tourPrices } = pageToEdit
    const cardObj = {
      title: '',
      subtitle: '',
      cta: {},
      image: {}
    }
    setPageToEdit({
      ...pageToEdit,
      tours_prices: {
        ...tourPrices,
        cards: [
          ...tourPrices.cards,
          cardObj
        ]
      }
    })
  }

  const handleChatbotStatus = status => {
    setPageToEdit({
      ...pageToEdit,
      show_chatbot: status
    })
  }

  const handleOptinFormStatus = status => {
    setPageToEdit({
      ...pageToEdit,
      show_optin_form: status
    })
  }

  const handleShowInInternalSearchChange = event => {
    setPageToEdit({
      ...pageToEdit,
      show_in_internal_search: event.target.checked
    })
  }

  const handleShowInGoogleSearchChange = event => {
    setPageToEdit({
      ...pageToEdit,
      show_in_google_search: event.target.checked
    })
  }

  const handleEvergreenChange = event => {
    setPageToEdit({
      ...pageToEdit,
      is_evergreen: event.target.checked
    })
  }

  const handleChatPopUpIdChange = event => {
    setPageToEdit({
      ...pageToEdit,
      chat_pop_up_id: event.target.value
    })
  }

  const handleAddUniversalModule = moduleType => {
    setPageToEdit({
      ...pageToEdit,
      modules: [...(pageToEdit.modules || []), createEmptyUniversalModule(moduleType)]
    })
  }

  const handleDeleteUniversalModule = (event, index) => {
    event.stopPropagation()
    const modules = [...(pageToEdit.modules || [])]
    modules.splice(index, 1)
    setPageToEdit({
      ...pageToEdit,
      modules
    })
  }

  const handleUniversalModuleUpdate = (index, updated) => {
    const modules = [...(pageToEdit.modules || [])]
    modules[index] = updated
    setPageToEdit({
      ...pageToEdit,
      modules
    })
  }

  const handleExpiresAtChange = (momentValue) => {
    setPageToEdit({
      ...pageToEdit,
      expires_at: momentValue ? momentValue.toISOString() : null
    })
  }

  const handleUniversalModuleReorder = (index, direction) => {
    const modules = [...(pageToEdit.modules || [])]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    if (swapIndex < 0 || swapIndex >= modules.length) return
    ;[modules[index], modules[swapIndex]] = [modules[swapIndex], modules[index]]
    setPageToEdit({ ...pageToEdit, modules })
  }

  const isUniversalTemplate = pageToEdit?.type === 'universal-template'
  const isUniversalTemplateExpired = (() => {
    if (!isUniversalTemplate) return false
    if (pageToEdit?.is_evergreen) return false
    if (!pageToEdit?.expires_at) return false
    const pageMs = new Date(pageToEdit.expires_at).getTime()
    if (Number.isNaN(pageMs)) return false
    return pageMs < Date.now()
  })()
  const universalTemplateErrors = isUniversalTemplate ? {
    name: !pageToEdit?.name?.trim() ? 'Name is required' : '',
    venueId: !pageToEdit?.venue_id ? 'Venue is required' : '',
    slug: !isValidUniversalTemplateSlug(pageToEdit?.slug, pageToEdit?.subtype)
      ? `Slug must be ${getUniversalTemplateSlugPrefix(pageToEdit?.subtype)}<valid-slug>`
      : '',
    pageExpiresAt: pageToEdit?.is_evergreen || pageToEdit?.expires_at ? '' : 'Page expiry time is required',
    expiresAt: (() => {
      if (pageToEdit?.is_evergreen) return ''
      const formMod = (pageToEdit?.modules || []).find(m => m.type === 'form')
      if (!formMod?.form) return ''
      const formExpires = formMod.form.expires_at
      if (!formExpires) return 'Form expiry time is required'
      const pageExpires = pageToEdit?.expires_at
      if (pageExpires) {
        const formMs = new Date(formExpires).getTime()
        const pageMs = new Date(pageExpires).getTime()
        if (!Number.isNaN(formMs) && !Number.isNaN(pageMs) && formMs >= pageMs) {
          return 'Form expiry time must be before page expiry time'
        }
      }
      return ''
    })(),
    formCustomFields: (() => {
      const formMod = (pageToEdit?.modules || []).find(m => m.type === 'form')
      const cf = formMod?.form?.custom_fields
      if (!cf || typeof cf !== 'object') return ''
      return Object.keys(cf).some(k => !String(k).trim())
        ? 'Each custom field must have a key'
        : ''
    })(),
    formUrl: (() => {
      const formMod = (pageToEdit?.modules || []).find(m => m.type === 'form')
      if (!formMod?.form) return ''
      return getFormUrlValidationMessage(formMod.form.url)
    })(),
    modules: (pageToEdit?.modules || []).length === 0 ? 'At least one module is required' : ''
  } : {}
  const updatePageButton = (
    <Button className={styles.updateActionButton} onClick={handleSubmit} type='primary'>
      Update
    </Button>
  )
  const deleteUniversalPageButton = isUniversalTemplate && isContentEditor ? (
    <Button
      className={`${styles.updateActionButton} ${styles.deleteUniversalPageButton}`}
      onClick={confirmDeleteUniversalPage}
      type='default'
    >
      Delete page
    </Button>
  ) : null
  const toggleSwitch = <Switch checked={pageToEdit?.show_chatbot ?? false} checkedChildren="Show Satisfi" defaultChecked onChange={handleChatbotStatus} unCheckedChildren="Hide Satisfi" />
  const optinFormToggleSwitch = (
    <Switch
      checked={pageToEdit?.show_optin_form ?? false}
      checkedChildren="Show opt-in form"
      onChange={handleOptinFormStatus}
      unCheckedChildren="Hide opt-in form"
    />
  )

  console.log('handleSelectedEventsChange ===> ', vlpHeroAllEvents, vlpHeroPromotedEvents)
  return (
    <Layout className={styles['edit-page-container']}>
      {isUniversalTemplateExpired && (
        <Alert banner className={styles.expiredBanner} message='Expired' showIcon={false} type='warning' />
      )}
      <PageHeader
        className={styles.headline}
        extra={(
          <Space align="center" size="middle" wrap>
            {toggleSwitch}
            {optinFormToggleSwitch}
            {deleteUniversalPageButton}
            {updatePageButton}
          </Space>
        )}
        title='Update Page'
      />
      <Layout.Content>
        <div>
          <PageBasicInfo
            addContentModule={addContentModule}
            addGallery={addGallery}
            addNewCategoryPill={addNewCategoryPill}
            addNewContentCardPill={addNewContentCardPill}
            addNewFAQ={addNewFAQ}
            addNewFAQImage={addNewFAQImage}
            addNewLink={addNewLink}
            addNewMarketingModule={addNewMarketingModule}
            addNewMarketingPill={addNewMarketingPill}
            addNewMediaIcon={addNewMediaIcon}
            addNewPriceCard={addNewPriceCard}
            addPriceCard={addPriceCard}
            addSuitesCardModule={addSuitesCardModule}
            allSuiteLevels={allSuiteLevels}
            deleteAllCategoryPills={deleteAllCategoryPills}
            deleteAllFAQs={deleteAllFAQs}
            deleteAllFAQsImages={deleteAllFAQsImages}
            deleteAllGallery={deleteAllGallery}
            deleteAllLink={deleteAllLink}
            deleteAllMarketingModules={deleteAllMarketingModules}
            deleteAllMarketingPills={deleteAllMarketingPills}
            deleteAllMediaIcon={deleteAllMediaIcon}
            deleteAllPriceCard={deleteAllPriceCard}
            deleteAllPriceCards={deleteAllPriceCards}
            deleteContentModule={deleteContentModule}
            deleteGallery={deleteGallery}
            deleteSuitesCardModule={deleteSuitesCardModule}
            galleries={gallery}
            gallery_header={gallerySectionTitle || ''}
            handleAddCta={handleAddCta}
            handleAddUniversalModule={handleAddUniversalModule}
            handleAllEventsChange={handleAllEventsChange}
            handleAutoplay={handleAutoplay}
            handleButtonTextUpdate={handleButtonTextUpdate}
            handleCTAHrefChange={handleCTAHrefChange}
            handleCTATitleChange={handleCTATitleChange}
            handleCalendarDescriptionChange={handleCalendarDescriptionChange}
            handleCalendarTitleChange={handleCalendarTitleChange}
            handleCategoryPills={handleCategoryPills}
            handleChatPopUpIdChange={handleChatPopUpIdChange}
            handleContentCard={handleContentCard}
            handleContentCardSectionHeaderChange={handleContentCardSectionHeaderChange}
            handleContentModuleCTANameChange={handleContentModuleCTANameChange}
            handleContentModuleCTAUrlChange={handleContentModuleCTAUrlChange}
            handleContentModuleContentChange={handleContentModuleContentChange}
            handleContentModuleHeaderChange={handleContentModuleHeaderChange}
            handleContentModuleImageUrlUpdate={handleContentModuleImageUrlUpdate}
            handleDeleteCta={handleDeleteCta}
            handleDeleteUniversalModule={handleDeleteUniversalModule}
            handleEvergreenChange={handleEvergreenChange}
            handleExpiresAtChange={handleExpiresAtChange}
            handleFAQAttending={handleFAQAttending}
            handleFAQHeroImageUpload={handleFAQHeroImageUpload}
            handleFAQHeroTitleChange={handleFAQHeroTitleChange}
            handleFAQPageSections={handleFAQPageSections}
            handleFAQs={handleFAQs}
            handleFAQsSectionHeaderChange={handleFAQsSectionHeaderChange}
            handleFlockler={handleFlockler}
            handleFormModuleDescriptionChange={handleFormModuleDescriptionChange}
            handleFormModuleDisclaimerChange={handleFormModuleDisclaimerChange}
            handleFormModuleHeaderChange={handleFormModuleHeaderChange}
            handleFormModuleIdUpdate={handleFormModuleIdUpdate}
            handleFormModuleMessageChange={handleFormModuleMessageChange}
            handleFormModuleSuccessSubmitButtonTextUpdate={handleFormModuleSuccessSubmitButtonTextUpdate}
            handleFormModuleTitleChange={handleFormModuleTitleChange}
            handleGalleryChange={handleGalleryChange}
            handleGalleryImageAltChange={handleGalleryImageAltChange}
            handleGalleryImageUrlUpdate={handleGalleryImageUrlUpdate}
            handleGalleryMediaChange={handleGalleryMediaChange}
            handleGallerySectionTitle={handleGallerySectionTitle}
            handleGallerySubTitleChange={handleGallerySubTitleChange}
            handleGalleryTitleChange={handleGalleryTitleChange}
            handleHeroButtonLabelChange={handleHeroButtonLabelChange}
            handleHeroChatLabelChange={handleHeroChatLabelChange}
            handleHeroDescriptionChange={handleHeroDescriptionChange}
            handleHeroEmailChange={handleHeroEmailChange}
            handleHeroImageUrlUpdate={handleHeroImageUrlUpdate}
            handleHeroPhoneNumberChange={handleHeroPhoneNumberChange}
            handleHeroSubTitleChange={handleHeroSubTitleChange}
            handleHeroTitleChange={handleHeroTitleChange}
            handleHorizontalBanner={handleHorizontalBanner}
            handleImageChange={handleImageChange}
            handleImageUrlChange={handleImageUrlChange}
            handleMarketingPills={handleMarketingPills}
            handleMediaRow={handleMediaRow}
            handleOverviewDescriptionChange={handleOverviewDescriptionChange}
            handleOverviewSectionHeaderChange={handleOverviewSectionHeaderChange}
            handlePageMetaDescriptionChange={handlePageMetaDescriptionChange}
            handlePageMetaHeadingChange={handlePageMetaHeadingChange}
            handlePageMetaKeywordsChange={handlePageMetaKeywordsChange}
            handlePageMetaShowSEOChange={handlePageMetaShowSEOChange}
            handlePageMetaTextChange={handlePageMetaTextChange}
            handlePageMetaTitleChange={handlePageMetaTitleChange}
            handlePageNameChange={handlePageNameChange}
            handlePageSubtypeChange={handlePageSubtypeChange}
            handlePageTypeChange={handlePageTypeChange}
            handlePriceCard={handlePriceCard}
            handlePriceCardDisclaimerChange={handlePriceCardDisclaimerChange}
            handlePriceCardSectionHeaderChange={handlePriceCardSectionHeaderChange}
            handleRecommendationSectionTitle={handleRecommendationSectionTitle}
            handleSelectedEventsChange={handleSelectedEventsChange}
            handleShowCalendar={handleShowCalendar}
            handleShowInGoogleSearchChange={handleShowInGoogleSearchChange}
            handleShowInInternalSearchChange={handleShowInInternalSearchChange}
            handleSlugChange={handleSlugChange}
            handleSuiteAmenitiesChange={handleSuiteAmenitiesChange}
            handleSuiteCTAUpdate={handleSuiteCTAUpdate}
            handleSuiteCapacityChange={handleSuiteCapacityChange}
            handleSuiteCardShortNameChange={handleSuiteCardShortNameChange}
            handleSuiteCardTypeChange={handleSuiteCardTypeChange}
            handleSuiteEventIdeasChange={handleSuiteEventIdeasChange}
            handleSuiteEventTypeChange={handleSuiteEventTypeChange}
            handleSuiteFormStatus={handleSuiteFormStatus}
            handleSuiteHospitalityChange={handleSuiteHospitalityChange}
            handleSuiteLogoUpload={handleSuiteLogoUpload}
            handleSuiteSizeChange={handleSuiteSizeChange}
            handleSuiteSubTitleChange={handleSuiteSubTitleChange}
            handleSuiteTitleChange={handleSuiteTitleChange}
            handleTourPlanDescriptionChange={handleTourPlanDescriptionChange}
            handleUniversalModuleReorder={handleUniversalModuleReorder}
            handleUniversalModuleUpdate={handleUniversalModuleUpdate}
            handleUpcomingShowsSectionTitle={handleUpcomingShowsSectionTitle}
            handleVenueChange={handleVenueChange}
            mode='edit'
            page={pageToEdit}
            pageTypes={PAGE_TYPES}
            recommendedEvents={recommendedEvents}
            reorderVLPHeroPromotedEvents={reorderVLPHeroPromotedEvents}
            selectedType={pageToEdit?.type}
            setAnchorLinks={setAnchorLinks}
            setGallery={setGallery}
            setMarketingModules={handleMarketingModules}
            setVLPHeroAllEvents={setVLPHeroAllEvents}
            setVLPHeroPromotedEvents={setVLPHeroPromotedEvents}
            suitesAmenities={suitesAmenities}
            suitesCapacity={suitesCapacity}
            suitesEventIdeas={suitesEventIdeas}
            suitesEventType={suitesEventType}
            suitesHospitality={suitesHospitality}
            suitesSize={suitesSize}
            todaysEvent={todaysEvent}
            tourVenues={tourVenues}
            universalTemplateErrors={universalTemplateErrors}
            upcomingEvents={upcomingEvents}
            venues={venues}
            vlpHeroAllEvents={vlpHeroAllEvents}
            vlpHeroPromotedEvents={vlpHeroPromotedEvents}
          />
        </div>
        <Spinner isLoading={!isDataLoaded}/>
        {
          updateStatus &&
          <ResultModal
            actions={[
              goToListPagesButton,
              keepEditingPage
            ]}
            handleCancel={handleModalClose}
            status="success"
            title={updateMessage}
          />
        }
      </Layout.Content>
    </Layout>
  )
}

EditPage.propTypes = {
  allSuiteLevels: PropTypes.array,
  availableEventsForPromotion: PropTypes.array,
  fetchPage: PropTypes.func,
  gallery: PropTypes.array,
  galleryHeader: PropTypes.string,
  newSuiteCardModule: PropTypes.func,
  page: PropTypes.object,
  promotedEvents: PropTypes.array,
  recommendedEvents: PropTypes.array,
  removePageById: PropTypes.func,
  resetUpdatePageStatus: PropTypes.func,
  route: PropTypes.object.isRequired,
  suitesAmenities: PropTypes.array.isRequired,
  suitesCapacity: PropTypes.array.isRequired,
  suitesEventIdeas: PropTypes.array.isRequired,
  suitesEventType: PropTypes.array.isRequired,
  suitesHospitality: PropTypes.array.isRequired,
  suitesSize: PropTypes.array.isRequired,
  todaysEvent: PropTypes.array,
  tourVenues: PropTypes.array,
  upcomingEvents: PropTypes.array,
  updateMessage: PropTypes.string,
  updatePage: PropTypes.func,
  updateStatus: PropTypes.bool,
  venues: PropTypes.array.isRequired,
  vlpHeroAllEvents: PropTypes.array
}

const mapStateToProps = ({ pages, suiteCards }) => ({
  page: pages.page,
  gallery: pages.gallery,
  galleryHeader: pages.gallery_header,
  updateStatus: pages.success,
  updateMessage: pages.message,
  recommendedEvents: pages.recommendedEvents,
  vlpHeroAllEvents: pages.vlpHeroAllEvents,
  promotedEvents: pages.promotedEvents,
  todaysEvent: pages.todaysEvent,
  upcomingEvents: pages.upcomingEvents,
  suitesAmenities: suiteCards.suitesAmenities,
  suitesCapacity: suiteCards.suitesCapacity,
  suitesEventIdeas: suiteCards.suitesEventIdeas,
  suitesEventType: suiteCards.suitesEventType,
  suitesHospitality: suiteCards.suitesHospitality,
  suitesSize: suiteCards.suitesSize,
  availableEventsForPromotion: suiteCards.allEvents,
  allSuiteLevels: suiteCards.allSuiteLevels,
  tourVenues: pages.tourVenues,
  venues: pages.allVenues
})

const mapDispatchToProps = {
  fetchPage: fetchPageById,
  newSuiteCardModule,
  removePageById,
  resetUpdatePageStatus,
  updatePage
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditPage)
